// Whether the pipeline the Studio has open has been changed since it was
// opened. On a deployed (but not running) pipeline those changes are
// local to the browser — the pipeline stays deployed, and the header
// marks them with an asterisk until they're saved elsewhere (duplicate)
// or discarded.
// The comparison is against a snapshot taken at open time: the code the
// editor was loaded with, the input document and the staged test suite.
// Code compares as a document, not as text, so a switch between the
// Stages and Full pipeline views (which regenerates the text) or a
// reformat doesn't count as an edit.
export interface OpenedSnapshot {
  code: string;
  input: string;
  suiteId: string | null;
}

export function editedSinceOpen(
  current: OpenedSnapshot,
  opened: OpenedSnapshot,
): boolean {
  if (current.input !== opened.input) return true;
  if (current.suiteId !== opened.suiteId) return true;
  return !sameDocument(current.code, opened.code);
}

/** Two pipeline texts holding the same JSON document (key order and
 *  whitespace aside); texts that don't parse compare as text. */
function sameDocument(a: string, b: string): boolean {
  if (a === b) return true;
  const docA = parse(a);
  const docB = parse(b);
  if (docA === undefined || docB === undefined) return false;
  return canonical(docA) === canonical(docB);
}

function parse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// JSON with object keys sorted at every level, so equal documents
// stringify equally whatever order their keys were written in.
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    isPlainObject(v)
      ? Object.fromEntries(
          Object.entries(v).sort(([x], [y]) => x.localeCompare(y)),
        )
      : v,
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** What the studio holds unsaved, for the leave guard (LeaveGuard.tsx):
 *  a draft edited since it was opened or last saved, scratch work — nothing
 *  open, something in the editors — or a deployed pipeline with local
 *  edits, which live only in the browser and are lost on leave. */
export function unsavedWorkKind(
  openKind: "draft" | "deployed" | null,
  editedSinceOpen: boolean,
  hasUnsavedWork: boolean,
): "draft" | "scratch" | "deployed" | null {
  if (openKind === "draft") return editedSinceOpen ? "draft" : null;
  if (openKind === null) return hasUnsavedWork ? "scratch" : null;
  return editedSinceOpen ? "deployed" : null;
}

/** The "Open pipeline" trigger's label: the open pipeline's name, starred
 *  while the editor holds local edits that aren't saved anywhere (Figma
 *  14896-124011); the generic label while nothing is open. */
export function openPipelineLabel(
  name: string | null,
  edited: boolean,
): string {
  if (name === null) return "Open pipeline";
  return edited ? `${name}*` : name;
}
